import { useState } from 'react';
import { Lock, Eye, EyeOff, KeyRound, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ChangePasswordCardProps {
  email: string;
}

export function ChangePasswordCard({ email }: ChangePasswordCardProps) {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  const handleChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!email) {
      toast.error('No email on account');
      return;
    }
    setLoading(true);
    try {
      // Verify current password by re-authenticating
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (signInError) {
        toast.error('Current password is incorrect');
        setLoading(false);
        return;
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Password updated successfully ✓');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!email) return;
    setSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success('Reset link sent! Check your email ✉️');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send reset email');
    } finally {
      setSendingReset(false);
    }
  };

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="w-full h-12 text-base font-display font-bold rounded-xl border-2"
      >
        <KeyRound className="w-4 h-4 mr-2" />
        Change Password
      </Button>
    );
  }

  return (
    <div className="border-2 rounded-xl p-4 space-y-3 bg-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-base">Change Password</h3>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
      <form onSubmit={handleChange} className="space-y-3">
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="Current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            className="pl-10 pr-10 h-11 rounded-xl border-2 font-body"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="New password (min 8 chars)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            className="pl-10 h-11 rounded-xl border-2 font-body"
          />
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            className="pl-10 h-11 rounded-xl border-2 font-body"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-11 font-display font-bold rounded-xl"
        >
          {loading ? 'Updating…' : 'Update Password'}
        </Button>
      </form>
      <div className="pt-2 border-t">
        <button
          type="button"
          onClick={handleSendResetEmail}
          disabled={sendingReset}
          className="w-full text-xs text-primary hover:underline font-body flex items-center justify-center gap-1.5 py-1"
        >
          <Mail className="w-3 h-3" />
          {sendingReset ? 'Sending…' : 'Forgot current password? Email me a reset link'}
        </button>
      </div>
    </div>
  );
}
